import { Button, Flex, Text } from '@chakra-ui/react';
import type { CollabDeliveryMode } from '@fluenceur/types';
import { Link as LinkIcon, MailOut, SendDiagonal } from 'iconoir-react';
import { useState, type ComponentType, type SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { useInvitationStoreSelector } from '@/contexts/invitation-context';
import { isReachable } from '../outreach/collab-recipient-helpers';
import { CollabDeliveryBanner } from './collab-delivery-banner';
import {
  CollabProposalEmailModule,
  type CollabProposalEmailState,
} from './collab-proposal-email-module';
import { useCollabRecipients, type CollabWizardRecipient } from './use-collab-recipients';
import { useCollabWizardSend } from './use-collab-wizard-send';

const K = 'invitations.modal.collab.wizard.delivery';

interface CollabDeliveryStepProps {
  onClose: () => void;
}

/** Muted one-clause note line of the delivery step. */
const DeliveryNote = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize="11.5px" color="fg.muted" lineHeight="1.5">
    {children}
  </Text>
);

/** Email mode body: the shared compose module plus its notes (bulk hint,
 * recipients without an email, and the free-message note). */
const EmailDeliveryBody = ({
  recipients,
  onStateChange,
}: {
  recipients: CollabWizardRecipient[];
  onStateChange: (state: CollabProposalEmailState) => void;
}) => {
  const { t } = useTranslation();
  const isBulk = recipients.length > 1;
  const missingEmailCount = recipients.filter((r) => isReachable(r) && !r.email).length;
  return (
    <>
      <CollabProposalEmailModule onStateChange={onStateChange} />
      <Flex direction="column" gap="1">
        {isBulk && <DeliveryNote>{t(`${K}.hintBulk`)}</DeliveryNote>}
        {isBulk && missingEmailCount > 0 && (
          <DeliveryNote>{t(`${K}.missingEmails`, { count: missingEmailCount })}</DeliveryNote>
        )}
        <DeliveryNote>{t(`${K}.freeMessageNote`)}</DeliveryNote>
      </Flex>
    </>
  );
};

const CTA_ICON: Record<CollabDeliveryMode, ComponentType<SVGProps<SVGSVGElement>>> = {
  link: LinkIcon,
  email: MailOut,
  dm: SendDiagonal,
};

/**
 * Delivery step of the sponsored wizard, symmetric across the three modes. The
 * recipient banner leads (with "Change" back to the channel radios, keeping the
 * composed state). Email adds the full compose surface; link and DM show a
 * short explainer. The CTA creates-and-sends the whole batch with the explicit
 * `delivery` mode; the composed body ships only in email mode.
 */
const CollabDeliveryStep = ({ onClose }: CollabDeliveryStepProps) => {
  const { t } = useTranslation();
  const collabDeliveryMode = useInvitationStoreSelector((state) => state.collabDeliveryMode);
  const setCurrentView = useInvitationStoreSelector((state) => state.setCurrentView);
  // A proposal answering a brief application always reaches the applicant's
  // account by email too, whatever channel the brand picks here.
  const answersApplication = useInvitationStoreSelector(
    (state) => !!state.collabBriefApplicationId
  );
  const recipients = useCollabRecipients();
  const { send, isSending } = useCollabWizardSend();

  const [emailState, setEmailState] = useState<CollabProposalEmailState>({
    proposalEmail: null,
    isValid: true,
  });

  // The channel step always commits a mode before navigating here; the link
  // fallback only covers a stale store state and stays sendable.
  const mode: CollabDeliveryMode = collabDeliveryMode ?? 'link';
  const isBulk = recipients.length > 1;
  const sendableCount = recipients.filter(isReachable).length;
  // Identity resolution still in flight on some lines: sending now would
  // misclassify them as unreachable (definitive per-line failures).
  const isResolving = recipients.some((r) => r.channel === 'resolving');

  const ctaLabel =
    mode === 'email'
      ? t(`${K}.ctaEmail`)
      : mode === 'dm'
        ? t(`${K}.ctaDm`)
        : isBulk
          ? t('invitations.modal.collab.wizard.send.ctaBulk', { count: sendableCount })
          : t('invitations.modal.collab.wizard.send.cta');
  const CtaIcon = CTA_ICON[mode];

  return (
    <Flex direction="column" gap="3">
      <CollabDeliveryBanner
        mode={mode}
        recipients={recipients}
        onChangeChannel={() => setCurrentView('collabSend')}
      />

      {mode === 'email' ? (
        <EmailDeliveryBody recipients={recipients} onStateChange={setEmailState} />
      ) : (
        <DeliveryNote>{t(mode === 'dm' ? `${K}.dmExplainer` : `${K}.linkExplainer`)}</DeliveryNote>
      )}

      {mode === 'link' && answersApplication && (
        <DeliveryNote>{t(`${K}.linkApplicantEmailNote`)}</DeliveryNote>
      )}

      {isResolving && (
        <DeliveryNote>{t('invitations.modal.collab.wizard.send.resolvingRecipients')}</DeliveryNote>
      )}

      <Flex
        direction={{ base: 'column-reverse', sm: 'row' }}
        align={{ base: 'stretch', sm: 'center' }}
        justify="flex-end"
        gap="2"
        pt="1"
      >
        <Button
          variant="outline"
          w={{ base: 'full', sm: 'auto' }}
          maxW="full"
          onClick={onClose}
          disabled={isSending}
        >
          {t('common.cancel')}
        </Button>
        <Button
          colorPalette="primary"
          w={{ base: 'full', sm: 'auto' }}
          maxW="full"
          minH="10"
          h={{ base: 'auto', sm: '10' }}
          py="2"
          whiteSpace={{ base: 'normal', sm: 'nowrap' }}
          loading={isSending}
          disabled={
            sendableCount === 0 ||
            isResolving ||
            // Email mode never ships without a body: while the template loads
            // (or with an empty editor) the payload is null and the CTA waits.
            (mode === 'email' && (!emailState.proposalEmail || !emailState.isValid))
          }
          onClick={() =>
            void send(
              recipients,
              mode,
              mode === 'email' ? (emailState.proposalEmail ?? undefined) : undefined
            )
          }
        >
          <CtaIcon width={15} height={15} />
          {ctaLabel}
        </Button>
      </Flex>
    </Flex>
  );
};

export default CollabDeliveryStep;
